import React, { useState, useEffect } from "react";
import DoneIcon from "@mui/icons-material/Done";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import ThemePreview from "./ThemePreview";
import { colorOptions, fontOptions, defaultTheme } from "./themeConstants";
import CommonButtons from "../../../components/CommonButtons";
import { notifyError, notifySuccess } from "../../../../assets/toastService";
import { Theme } from "../types/profile";
import useUsers from "src/business-types/grocery/hooks/useUsers";

interface ThemeCustomizerProps {
  theme: Theme | null;
  logo: string;
  userName: string;
  businessName: string;
  onUpdateTheme: (theme: Theme) => Promise<{ success: boolean }>;
  onResetTheme: () => Promise<{ success: boolean }>;
}

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ theme, logo, userName, businessName, onUpdateTheme, onResetTheme }) => {
  const [selectedBackground, setSelectedBackground] = useState(defaultTheme.backgroundColor);
  const [selectedFontColor, setSelectedFontColor] = useState(defaultTheme.fontColor);
  const [selectedFontFamily, setSelectedFontFamily] = useState(defaultTheme.fontFamily);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hook to reload user context data dynamically
  const { reload } = useUsers();

  useEffect(() => {
    if (theme) {
      setSelectedBackground(theme.backgroundColor);
      setSelectedFontColor(theme.fontColor);
      setSelectedFontFamily(theme.fontFamily);
    }
  }, [theme]);

  const handleColorSelect = (colorKey: string) => {
    setSelectedBackground(colorKey);
    const classString = colorOptions[colorKey] || "";
    const textClass = classString.match(/text-(#[0-9A-Fa-f]{6}|\w+)/)?.[1] || "white";
    setSelectedFontColor(textClass);
  };

  const handleFontSelect = (fontKey: string) => setSelectedFontFamily(fontKey);

  const handleUpdate = async () => {
    setIsSubmitting(true);
    const newTheme: Theme = {
      backgroundColor: selectedBackground,
      fontColor: selectedFontColor,
      fontFamily: selectedFontFamily,
      businessName,
    };
    
    try {
      const result = await onUpdateTheme(newTheme);
      if (result?.success) {
        notifySuccess("Theme updated successfully!");
        // Refresh user data without page reload
        reload();
      } else {
        notifyError("Failed to update theme.");
      }
    } catch (error) {
      console.error(error);
      notifyError("Failed to update theme.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsSubmitting(true);
    setSelectedBackground(defaultTheme.backgroundColor);
    setSelectedFontColor(defaultTheme.fontColor);
    setSelectedFontFamily(defaultTheme.fontFamily);

    try {
      const result = await onResetTheme();
      if (result?.success) {
        notifySuccess("Theme reset to default!");
        // Refresh user data after reset
        reload();
      } else {
        notifyError("Failed to reset theme.");
      }
    } catch (error) {
      console.error(error);
      notifyError("Failed to reset theme.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full p-2 md:p-4 overflow-y-auto">
      {/* Left: Controls */}
      <div className="w-full lg:w-7/12 space-y-6">
        {/* Colors Section */}
        <div className="space-y-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
            Theme Colors
          </h3>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 xl:grid-cols-8 gap-3">
            {Object.entries(colorOptions).map(([name, className]) => (
              <button
                key={name}
                type="button"
                aria-label={`Select color ${name}`}
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl cursor-pointer transition-transform hover:scale-105 active:scale-95 shadow-sm flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${className}`}
                onClick={() => handleColorSelect(name)}
              >
                {selectedBackground === name && (
                  <DoneIcon className="text-white text-xl sm:text-2xl drop-shadow-md" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Font Style Section */}
        <div className="space-y-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
            Font Styles
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[240px] overflow-y-auto pr-1 scrollbar-thin">
            {Object.entries(fontOptions).map(([name, className]) => (
              <button
                key={name}
                type="button"
                className={`flex items-center w-full p-3 border rounded-lg cursor-pointer transition-all duration-150 text-left focus:outline-none ${
                  selectedFontFamily === name
                    ? "bg-blue-50 border-blue-500 text-blue-700 font-medium shadow-sm"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => handleFontSelect(name)}
              >
                {selectedFontFamily === name ? (
                  <CircleIcon className="text-blue-600 mr-2.5 text-xs sm:text-sm shrink-0" />
                ) : (
                  <CircleOutlinedIcon className="text-gray-400 mr-2.5 text-xs sm:text-sm shrink-0" />
                )}
                <span className={`text-sm truncate ${className}`}>{name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Preview & Actions */}
      <div className="w-full lg:w-5/12 flex flex-col justify-between space-y-6 lg:space-y-0 lg:sticky lg:top-4 h-fit">
        <div className="space-y-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 lg:hidden">
            Live Preview
          </h3>
          <ThemePreview
            theme={{
              backgroundColor: selectedBackground,
              fontColor: selectedFontColor,
              fontFamily: selectedFontFamily,
              businessName,
            }}
            logo={logo}
            userName={userName}
            businessName={businessName}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100 lg:border-none">
          <CommonButtons
            label="Reset Theme"
            onClick={handleReset}
            variant="secondary"
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          />
          <CommonButtons
            label="Update Theme"
            onClick={handleUpdate}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          />
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;